import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { opsApi, money } from "../lib/api";

const pct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;
const hrs = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
const deltaFmt = (v) => (Math.abs(v) < 0.005 ? "—" : `${v > 0 ? "+" : "−"}${money(Math.abs(v))}`);
const deltaTone = (v) => (v > 0.005 ? "text-emerald-700" : v < -0.005 ? "text-rose-700" : "text-slate-400");

const COLS = [
  ["Item", "text-left px-4"], ["Manufacturer", "text-left"], ["Item #", "text-left"],
  ["Qty", "text-right"], ["Unit", "text-left"], ["Cost", "text-right"], ["Mat GM%", "text-right"],
  ["Hrs/Unit", "text-right"], ["Total Hrs", "text-right"], ["Lab GM%", "text-right"],
  ["Labor", "text-right"], ["Cost Code", "text-left"], ["Total", "text-right"],
];

/** Original (frozen at Won) · Working (the living contract: Original + approved COs) · Delta. */
export default function EstimateTab({ project }) {
  const [view, setView] = useState("working");
  const [data, setData] = useState(null);
  const [showQuals, setShowQuals] = useState(false);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState([]);   // scopes to show; empty = all
  const [collapsed, setCollapsed] = useState([]);

  const load = useCallback(async () => {
    setData(null);
    try {
      setData(await opsApi.estimate(project.id, view));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load that estimate");
    }
  }, [project.id, view]);

  useEffect(() => { load(); }, [load]);

  const ready = data && data.view === view;
  const scopes = useMemo(() => {
    if (!ready) return [];
    const names = data.view === "delta" ? data.sections.map((s) => s.section)
      : Object.keys(data.sections || {});
    return names;
  }, [ready, data]);

  const filtered = useMemo(() => {
    if (!ready) return null;
    const q = search.trim().toLowerCase();
    const keep = (section) => !picked.length || picked.includes(section || "Unassigned");
    const hit = (l) => !q || [l.description, l.manufacturer, l.item_number, l.cost_code, l.section, l.co_number]
      .some((v) => String(v || "").toLowerCase().includes(q));
    if (data.view === "delta") {
      return { ...data,
        sections: data.sections.filter((s) => keep(s.section) && (!q || s.section.toLowerCase().includes(q))),
        co_lines: data.co_lines.filter((l) => keep(l.section) && hit(l)) };
    }
    return { ...data, lines: data.lines.filter((l) => keep(l.section) && hit(l)) };
  }, [ready, data, search, picked]);

  const toggleScope = (s) => setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));
  const toggleCollapsed = (s) => setCollapsed((c) => (c.includes(s) ? c.filter((x) => x !== s) : [...c, s]));
  const allCollapsed = scopes.length > 0 && scopes.every((s) => collapsed.includes(s));

  return (
    <div className="space-y-4" data-testid="ops-estimate-tab">
      {ready && <Cards totals={data.view === "delta" ? null : data.totals} delta={data.view === "delta" ? data : null} />}

      <div className="flex flex-wrap items-center gap-2" data-testid="ops-estimate-toolbar">
        {ready && data.view !== "delta" && (
          <button onClick={() => setCollapsed(allCollapsed ? [] : [...scopes])} data-testid="ops-estimate-collapse-all"
                  aria-label={allCollapsed ? "Expand all scopes" : "Collapse all scopes"} title={allCollapsed ? "Expand all" : "Collapse all"}
                  className="grid h-9 w-9 place-items-center rounded-lg bg-slate-100 text-slate-500 transition-colors hover:text-slate-900">
            {allCollapsed ? <ChevronsUpDown className="h-4 w-4" /> : <ChevronsDownUp className="h-4 w-4" />}
          </button>
        )}
        <label className="flex h-9 items-center gap-2 rounded-lg bg-slate-100 px-3 text-xs text-slate-600 focus-within:ring-2 focus-within:ring-slate-300">
          <Search className="h-3.5 w-3.5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the estimate"
                 data-testid="ops-estimate-search"
                 className="w-48 bg-transparent text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none" />
        </label>
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {[["original", "Original"], ["working", "Working"], ["delta", "Delta"]].map(([key, label]) => (
            <button key={key} onClick={() => setView(key)} data-testid={`ops-estimate-view-${key}`}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      view === key ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-800"}`}>
              {label}
            </button>
          ))}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="ops-estimate-scope-filter"
                    className={`flex h-9 items-center gap-1.5 rounded-lg bg-slate-100 px-3 text-xs font-medium transition-colors ${
                      picked.length ? "text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
              <Filter className="h-3.5 w-3.5" /> {picked.length === 1 ? picked[0] : "Scope"}
              {picked.length > 1 && <span className="text-slate-400">· {picked.length}</span>}
              <ChevronDown className="h-3 w-3 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel className="text-[11px]">Show only these scopes</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {scopes.map((s) => (
              <DropdownMenuCheckboxItem key={s} checked={picked.includes(s)} onCheckedChange={() => toggleScope(s)}
                                        onSelect={(e) => e.preventDefault()} data-testid={`ops-estimate-scope-${s}`}>
                {s}
              </DropdownMenuCheckboxItem>
            ))}
            {picked.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <button onClick={() => setPicked([])} data-testid="ops-estimate-scope-all"
                        className="w-full px-2 py-1.5 text-left text-xs text-slate-600 hover:bg-slate-50">Clear — show all</button>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {!ready ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading estimate…
        </div>
      ) : data.view === "delta" ? (
        <DeltaView data={filtered} />
      ) : (
        <LinesView data={filtered} view={view} showQuals={showQuals} setShowQuals={setShowQuals}
                   collapsed={collapsed} onToggle={toggleCollapsed} />
      )}
    </div>
  );
}

function Cards({ totals, delta }) {
  if (delta) {
    const d = delta.delta_totals || {};
    return (
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="ops-estimate-delta-cards">
        {[["Total Sales", d.total_sales], ["GM $", d.total_gm_dollars]].map(([k, v]) => (
          <div key={k} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
            <p className="text-xs text-slate-500">Δ {k}</p>
            <p className={`text-lg font-semibold ${deltaTone(v)}`}>{deltaFmt(Number(v || 0))}</p>
          </div>
        ))}
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
          <p className="text-xs text-slate-500">GM % · Original → Working</p>
          <p className="text-lg font-semibold text-slate-900">
            {pct(delta.original_totals?.total_gm_percent)} → {pct(delta.working_totals?.total_gm_percent)}
          </p>
        </div>
        {[["Material", d.total_material], ["Labor", d.total_labor]].map(([k, v]) => (
          <div key={k} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
            <p className="text-xs text-slate-500">Δ {k}</p>
            <p className={`text-lg font-semibold ${deltaTone(v)}`}>{deltaFmt(Number(v || 0))}</p>
          </div>
        ))}
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
          <p className="text-xs text-slate-500">Δ Hours</p>
          <p className={`text-lg font-semibold ${deltaTone(d.total_labor_hours)}`}>
            {Math.abs(d.total_labor_hours || 0) < 0.05 ? "—" : `${d.total_labor_hours > 0 ? "+" : ""}${hrs(d.total_labor_hours)}`}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="ops-estimate-cards">
      {[["Total Sales", money(totals.total_sales)], ["GM $", money(totals.total_gm_dollars)],
        ["GM %", pct(totals.total_gm_percent)], ["Material", money(totals.total_material)],
        ["Labor", money(totals.total_labor)], ["Hours", hrs(totals.total_labor_hours)]].map(([k, v]) => (
        <div key={k} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
          <p className="text-xs text-slate-500">{k}</p>
          <p className="text-lg font-semibold text-slate-900"
             data-testid={`ops-estimate-card-${k.replace(/[^a-z%]/gi, "").replace("%", "pct").toLowerCase()}`}>{v}</p>
        </div>
      ))}
    </div>
  );
}

function groupLines(lines) {
  const sections = {};
  for (const l of lines) {
    const key = l.section || "Unassigned";
    const s = (sections[key] = sections[key] || { base: [], cos: {} });
    if (l.co_id) {
      (s.cos[l.co_id] = s.cos[l.co_id] || { number: l.co_number, title: l.co_title, lines: [] }).lines.push(l);
    } else s.base.push(l);
  }
  return sections;
}

function ScopeChip({ label, value, testId }) {
  return (
    <span className="flex items-baseline gap-1.5 text-xs" data-testid={testId}>
      <span className="uppercase tracking-wide text-slate-400">{label}</span>
      <span className="font-semibold tabular-nums text-slate-900">{value}</span>
    </span>
  );
}

function QualList({ quals, testId }) {
  return (
    <ul className="space-y-1 text-sm text-slate-700" data-testid={testId}>
      {quals.map((q, i) => (
        <li key={q.id || i} className="flex gap-2">
          <span className="w-16 shrink-0 text-xs uppercase tracking-wide text-slate-400">{q.kind === "exclude" ? "Excludes" : q.kind === "include" ? "Includes" : "Qual"}</span>
          <span className="flex-1">{q.text}</span>
        </li>
      ))}
    </ul>
  );
}

function ScopeCard({ section, group, tot, open, onToggle, quals = [] }) {
  const coTotal = (lines) => lines.reduce((n, l) => n + Number(l.line_total || 0), 0);
  const [showQ, setShowQ] = useState(false);
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`ops-estimate-scope-card-${section}`}>
      <button onClick={onToggle} data-testid={`ops-estimate-section-${section}`}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-slate-50">
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
        <span className="font-mono text-[11px] text-slate-500">{tot.cost_code || "—"}</span>
        <span className="truncate text-sm font-semibold text-slate-900">{section}</span>
        <span className="ml-auto flex shrink-0 items-center gap-5">
          <ScopeChip label="Hrs" value={hrs(tot.hours)} />
          <ScopeChip label="Material" value={money(tot.material)} />
          <ScopeChip label="Labor" value={money(tot.labor)} />
          <ScopeChip label="Total" value={money(tot.sales)} testId={`ops-estimate-section-total-${section}`} />
          <ScopeChip label="GM" value={pct(tot.gm_pct)} />
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100">
          <table className="w-full table-fixed text-sm">
            <colgroup>
              <col className="w-[22rem]" /><col className="w-32" /><col className="w-28" /><col className="w-14" />
              <col className="w-14" /><col className="w-24" /><col className="w-20" /><col className="w-20" />
              <col className="w-20" /><col className="w-20" /><col className="w-24" /><col className="w-24" /><col className="w-28" />
            </colgroup>
            <thead className="bg-slate-50/60 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                {COLS.map(([label, cls]) => (
                  <th key={label} className={`px-3 py-1.5 whitespace-nowrap font-medium ${cls}`}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {group.base.map((l) => <LineRow key={l.id} l={l} />)}
              {Object.entries(group.cos).map(([coId, co]) => (
                <Fragment key={coId}>
                  <tr className="bg-slate-50" data-testid={`ops-estimate-co-group-${coId}`}>
                    <td colSpan={12} className="border-l-2 border-slate-900 px-4 py-1.5 text-xs text-slate-600">
                      <span className="font-semibold text-slate-900">{co.number}</span>
                      {co.title ? <span className="text-slate-500"> · {co.title}</span> : null}
                    </td>
                    <td className="bg-slate-50 px-3 py-1.5 text-right text-xs font-semibold tabular-nums text-slate-900">
                      {money(coTotal(co.lines))}
                    </td>
                  </tr>
                  {co.lines.map((l) => <LineRow key={l.id} l={l} co />)}
                </Fragment>
              ))}
            </tbody>
          </table>
          {quals.length > 0 && (
            <div className="border-t border-slate-100" data-testid={`ops-estimate-scope-quals-${section}`}>
              <button onClick={() => setShowQ((v) => !v)} data-testid={`ops-estimate-scope-quals-toggle-${section}`}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-semibold text-slate-600 hover:bg-slate-50">
                {showQ ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                Qualifications <span className="font-normal text-slate-400">{quals.length}</span>
              </button>
              {showQ && <div className="px-4 pb-3"><QualList quals={quals} /></div>}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function LinesView({ data, view, showQuals, setShowQuals, collapsed, onToggle }) {
  const sections = groupLines(data.lines);
  const allQuals = (data.qualifications || []).filter((q) => q.included !== false);
  const qualsFor = (section) => {
    const code = data.sections?.[section]?.cost_code || "";
    return allQuals.filter((q) => q.level !== "project" && ((q.section && q.section === section) || (!q.section && q.cost_code && q.cost_code === code)));
  };
  const scoped = new Set(Object.keys(sections).flatMap((sec) => qualsFor(sec).map((q) => q.id)));
  const quals = allQuals.filter((q) => !scoped.has(q.id));
  const shown = Object.keys(sections);
  const sum = (k) => shown.reduce((n, s) => n + Number(data.sections?.[s]?.[k] || 0), 0);
  const gTotal = sum("sales"), gGm = sum("gm_dollars");
  return (
    <>
      <div className="space-y-4" data-testid="ops-estimate-scroll">
        {Object.entries(sections).map(([section, group]) => (
          <ScopeCard key={section} section={section} group={group} tot={data.sections?.[section] || {}}
                     open={!collapsed.includes(section)} onToggle={() => onToggle(section)} quals={qualsFor(section)} />
        ))}
        {data.lines.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-8 text-center text-xs text-slate-500">No lines match.</div>
        )}
        {shown.length > 0 && (
          <div className="flex items-center gap-3 rounded-xl border border-slate-900 bg-slate-900 px-4 py-2.5 text-white"
               data-testid="ops-estimate-grand-total">
            <span className="text-sm font-semibold">{shown.length < Object.keys(data.sections || {}).length ? "Shown scopes" : "Estimate total"}</span>
            <span className="ml-auto flex items-center gap-5 [&_span]:text-white [&_.uppercase]:text-slate-400">
              <ScopeChip label="Hrs" value={hrs(sum("hours"))} />
              <ScopeChip label="Material" value={money(sum("material"))} />
              <ScopeChip label="Labor" value={money(sum("labor"))} />
              <ScopeChip label="Total" value={money(gTotal)} testId="ops-estimate-grand-total-sales" />
              <ScopeChip label="GM" value={pct(gTotal ? gGm / gTotal : 0)} />
            </span>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="ops-estimate-quals">
        <button onClick={() => setShowQuals((v) => !v)} data-testid="ops-estimate-quals-toggle"
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-slate-900">
          {showQuals ? <ChevronDown className="h-4 w-4 text-slate-400" />
            : <ChevronRight className="h-4 w-4 text-slate-400" />}
          Project Level Qualifications
          <span className="ml-auto text-xs font-normal text-slate-400">{quals.length}</span>
        </button>
        {showQuals && (
          <div className="border-t border-slate-100 px-4 py-3">
            {quals.length === 0 ? (
              <p className="text-xs text-slate-500">No project-level qualifications on this proposal.</p>
            ) : <QualList quals={quals} testId="ops-estimate-project-quals-list" />}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-500" data-testid="ops-estimate-footnote">
        {view === "original"
          ? "The original estimate as sold — frozen at Won. Nothing writes to it."
          : "The working estimate is the living contract: the original plus every change order that reaches Processed Approved. Nothing else writes to it."}
      </p>
    </>
  );
}

function LineRow({ l, co }) {
  const tone = l.internal_only ? "italic text-slate-400" : co ? "text-slate-600" : "text-slate-800";
  const dash = <span className="text-slate-300">—</span>;
  return (
    <tr className={`${tone} transition-colors hover:bg-slate-100`} data-testid={`ops-estimate-line-${l.id}`}>
      <td className={`truncate px-4 py-2 ${co ? "border-l-2 border-slate-200 pl-6" : ""}`} title={l.description || ""}
          data-testid={`ops-estimate-line-desc-${l.id}`}>{l.description || dash}</td>
      <td className="truncate px-3 py-2" title={l.manufacturer || ""}>{l.manufacturer || dash}</td>
      <td className="truncate px-3 py-2" title={l.item_number || ""}>{l.item_number || dash}</td>
      <td className="px-3 py-2 text-right tabular-nums">{l.qty}</td>
      <td className="px-3 py-2">{l.uom}</td>
      <td className="px-3 py-2 text-right tabular-nums">{money(l.unit_cost)}</td>
      <td className="px-3 py-2 text-right text-xs tabular-nums">{pct(l.material_gm)}</td>
      <td className="px-3 py-2 text-right text-xs tabular-nums">{hrs(l.labor_hours_per_unit)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{hrs(l.total_labor_hours)}</td>
      <td className="px-3 py-2 text-right text-xs tabular-nums">{pct(l.labor_gm)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{money(l.total_labor)}</td>
      <td className="px-3 py-2 font-mono text-[11px]">{l.cost_code || dash}</td>
      <td className="px-3 py-2 text-right font-medium tabular-nums">{money(l.line_total)}</td>
    </tr>
  );
}

function DeltaView({ data }) {
  const d = data.delta_totals || {};
  const cos = {};
  for (const l of data.co_lines || []) {
    (cos[l.co_id] = cos[l.co_id] || { number: l.co_number, title: l.co_title, total: 0, lines: [] });
    cos[l.co_id].total += Number(l.line_total || 0);
    cos[l.co_id].lines.push(l);
  }
  return (
    <>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="ops-estimate-delta-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Scope</th>
              <th className="px-3 py-2 text-right">Original</th>
              <th className="px-3 py-2 text-right">Working</th>
              <th className="px-3 py-2 text-right">Δ</th>
              <th className="px-3 py-2 text-right">Orig Hrs</th>
              <th className="px-3 py-2 text-right">Working Hrs</th>
              <th className="px-3 py-2 text-right">Δ Hrs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data.sections || []).map((s) => (
              <tr key={s.section} data-testid={`ops-estimate-delta-row-${s.section}`}>
                <td className="px-4 py-2 font-medium text-slate-800">{s.section}</td>
                <td className="px-3 py-2 text-right">{money(s.original)}</td>
                <td className="px-3 py-2 text-right">{money(s.working)}</td>
                <td className={`px-3 py-2 text-right font-medium ${deltaTone(s.delta)}`}>{deltaFmt(s.delta)}</td>
                <td className="px-3 py-2 text-right">{hrs(s.original_hours)}</td>
                <td className="px-3 py-2 text-right">{hrs(s.working_hours)}</td>
                <td className={`px-3 py-2 text-right ${deltaTone(s.delta_hours)}`}>
                  {Math.abs(s.delta_hours) < 0.05 ? "—" : `${s.delta_hours > 0 ? "+" : ""}${hrs(s.delta_hours)}`}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50 text-sm font-semibold text-slate-900">
            <tr>
              <td className="px-4 py-2">Total</td>
              <td className="px-3 py-2 text-right">{money(data.original_totals?.total_sales)}</td>
              <td className="px-3 py-2 text-right">{money(data.working_totals?.total_sales)}</td>
              <td className={`px-3 py-2 text-right ${deltaTone(d.total_sales)}`}>{deltaFmt(Number(d.total_sales || 0))}</td>
              <td className="px-3 py-2 text-right">{hrs(data.original_totals?.total_labor_hours)}</td>
              <td className="px-3 py-2 text-right">{hrs(data.working_totals?.total_labor_hours)}</td>
              <td className="px-3 py-2 text-right">{hrs(d.total_labor_hours)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="ops-estimate-delta-cos">
        <div className="px-4 py-2.5 text-sm font-semibold text-slate-900">Approved change orders on the working estimate</div>
        {Object.keys(cos).length === 0 ? (
          <p className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
            No approved change orders yet — Working equals Original.
          </p>
        ) : (
          <div className="divide-y divide-slate-100 border-t border-slate-100">
            {Object.entries(cos).map(([id, co]) => (
              <div key={id} className="px-4 py-2" data-testid={`ops-estimate-delta-co-${id}`}>
                <div className="flex items-baseline gap-2 text-sm">
                  <span className="font-medium text-slate-800">{co.number}</span>
                  <span className="text-slate-500">{co.title}</span>
                  <span className="ml-auto font-semibold text-slate-900">{money(co.total)}</span>
                </div>
                <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                  {co.lines.map((l) => (
                    <li key={l.id} className="flex gap-2">
                      <span className="w-28 shrink-0 text-slate-400">{l.section}</span>
                      <span className="flex-1 truncate" title={l.description}>{l.description}</span>
                      <span className="text-slate-400">{l.qty} {l.uom}</span>
                      <span className="w-24 text-right">{money(l.line_total)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
